import { useCallback, useEffect, useState } from "react";
import {
  FileText, FileSpreadsheet, Loader2, RefreshCw, Play,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import "./CommunityIntelligence.css";
import "./AlertsCenter.css";

const REPORT_CATALOG = [
  {
    kind: "weekly_community_intelligence_summary",
    title: "Weekly Community Intelligence Summary",
    description: "Headline KPIs, top counties, trend series, and emerging hotspots for the past 7 days.",
    window: 7,
  },
  {
    kind: "monthly_community_impact_report",
    title: "Monthly Community Impact Report",
    description: "Board-/funder-ready 30-day rollup with headline impact, top programs, and ROI proxy.",
    window: 30,
  },
  {
    kind: "resource_desert_report",
    title: "Resource Desert Report",
    description: "Complete Resource Desert Detector output — severe/desert/watch hotspots with score breakdown.",
    window: 90,
  },
  {
    kind: "referral_demand_forecast",
    title: "Referral Demand Forecast",
    description: "30-day linear projection using the last 90 days of referral history.",
    window: 90,
  },
  {
    kind: "capacity_planning_report",
    title: "Capacity Planning Report",
    description: "Program- and county-level capacity gap analysis using a 3:1 target ratio.",
    window: 60,
  },
  {
    kind: "funding_opportunity_report",
    title: "Funding Opportunity Report",
    description: "Prioritized investment opportunities in resource desert hotspots with impact potential.",
    window: 90,
  },
];

export default function ExecutiveReports() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/analytics/reports/history?limit=100");
      setHistory(data.reports || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (kind) => {
    setGenerating(kind); setError("");
    try {
      const { data } = await api.post("/analytics/reports/generate", { kind });
      setError("");
      await load();
      // Auto-download PDF for convenience
      download(data.id, "pdf");
    } catch (e) { setError(formatApiError(e)); }
    finally { setGenerating(null); }
  };

  const download = (reportId, format) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/reports/${reportId}.${format}?_t=${Date.now()}`;
    const token = localStorage.getItem("hw_access_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const filename = (r.headers.get("Content-Disposition") || "")
          .match(/filename="([^"]+)"/)?.[1] || `report.${format}`;
        return r.blob().then((b) => ({ blob: b, filename }));
      })
      .then(({ blob, filename }) => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objUrl);
      })
      .catch((e) => setError(`Download failed: ${e.message || e}`));
  };

  return (
    <div className="hw-ci" data-testid="executive-reports">
      <div className="hw-ci__eyebrow">
        <FileText className="w-3.5 h-3.5"
                    style={{ display: "inline", verticalAlign: "text-bottom",
                              marginRight: "0.3rem" }} />
        Community Intelligence · Executive Reports
      </div>
      <h1 className="hw-ci__title">Board-ready reports, one click away</h1>
      <p className="hw-ci__sub">
        Generate any of the six intelligence reports on demand. Each report is
        stored in history and can be downloaded again anytime as PDF or CSV.
      </p>

      <div className="hw-alerts__report-grid" data-testid="report-catalog">
        {REPORT_CATALOG.map((r) => (
          <div key={r.kind} className="hw-alerts__report-card"
                data-testid={`report-card-${r.kind}`}>
            <h3>{r.title}</h3>
            <p>{r.description}</p>
            <p style={{ fontSize: "0.7rem", color: "#94A3B8" }}>
              Default window: {r.window} days
            </p>
            <div className="hw-alerts__report-card-actions">
              <button className="hw-ci__btn hw-ci__btn--primary"
                      onClick={() => generate(r.kind)}
                      disabled={generating === r.kind}
                      data-testid={`generate-${r.kind}`}>
                {generating === r.kind ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <><Play className="w-3.5 h-3.5" /> Generate + Download</>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="hw-ci__panel" data-testid="report-history">
        <h2>
          <span>Report history</span>
          <small>{history.length} generated</small>
          <button className="hw-ci__btn" onClick={load} style={{ marginLeft: "auto" }}
                  data-testid="reports-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </h2>
        {error && <div style={{ color: "#B91C1C" }} data-testid="reports-error">{error}</div>}
        {loading && (
          <div style={{ color: "#0F766E" }}>
            <Loader2 className="w-4 h-4 animate-spin"
                      style={{ display: "inline", verticalAlign: "text-bottom" }} /> loading…
          </div>
        )}
        <table className="hw-ci__table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Generated</th>
              <th>Window</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} data-testid={`report-row-${h.id}`}>
                <td><strong>{h.kind.replace(/_/g, " ")}</strong></td>
                <td style={{ fontSize: "0.75rem", color: "#64748B" }}>
                  {(h.generated_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td>{h.window_days ?? "—"}d</td>
                <td style={{ display: "flex", gap: "0.3rem" }}>
                  <button className="hw-ci__btn" onClick={() => download(h.id, "csv")}
                          data-testid={`report-csv-${h.id}`}>
                    <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
                  </button>
                  <button className="hw-ci__btn hw-ci__btn--primary"
                          onClick={() => download(h.id, "pdf")}
                          data-testid={`report-pdf-${h.id}`}>
                    <FileText className="w-3.5 h-3.5" /> PDF
                  </button>
                </td>
              </tr>
            ))}
            {history.length === 0 && !loading && (
              <tr><td colSpan={4} style={{ textAlign: "center",
                                                padding: "0.9rem", color: "#64748B" }}>
                No reports generated yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
